// src/hooks/useUnreadMessagesCount.js - VERSION CORRIGÉE AVEC VÉRIFICATION DOSSIERS
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import eventBus from '../utils/eventBus';

const useUnreadMessagesCount = (userId, userEmail = '') => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      console.log('🚨 MESSAGES-DEBUG: userId manquant');
      setLoading(false);
      return;
    }

    console.log('🔄 MESSAGES-DEBUG: *** INITIALISATION HOOK ***');
    console.log('🔄 MESSAGES-DEBUG: userId:', userId);
    console.log('🔄 MESSAGES-DEBUG: userEmail:', userEmail);

    let unsubscribe = null;

    try {
      const discussionsRef = collection(db, 'discussions');
      
      // REQUÊTE SÉCURISÉE : seulement par participants
      const q = query(
        discussionsRef,
        where('participants', 'array-contains', userId)
      );

      console.log('🔄 MESSAGES-DEBUG: Configuration du listener onSnapshot...');

      unsubscribe = onSnapshot(q, async (querySnapshot) => {
        console.log('📡 MESSAGES-DEBUG: *** NOUVEAU SNAPSHOT REÇU ***');
        console.log('📡 MESSAGES-DEBUG: Nombre de discussions:', querySnapshot.docs.length);
        console.log('📡 MESSAGES-DEBUG: Timestamp:', new Date().toLocaleTimeString());
        
        let totalUnreadMessages = 0;
        let totalAutomaticMessages = 0;
        let debugInfo = [];
        
        // 🔧 NOUVELLE LOGIQUE : Vérifier chaque discussion avec son dossier
        for (const docSnapshot of querySnapshot.docs) {
          const data = docSnapshot.data();
          
          console.log(`📄 MESSAGES-DEBUG: === DISCUSSION ${docSnapshot.id} ===`);
          console.log(`📄 MESSAGES-DEBUG: Participants:`, data.participants);
          console.log(`📄 MESSAGES-DEBUG: dossierId:`, data.dossierId);
          
          // Vérification de sécurité
          if (!data.participants?.includes(userId)) {
            console.log('❌ MESSAGES-DEBUG: Discussion exclue (pas participant)');
            continue;
          }
          
          // 🔧 NOUVEAU : Vérifier si le dossier existe encore
          if (data.dossierId) {
            try {
              const dossierDoc = await getDoc(doc(db, 'dossiers', data.dossierId));
              if (!dossierDoc.exists()) {
                console.log('🗑️ MESSAGES-DEBUG: Discussion exclue (dossier supprimé)');
                console.log('🗑️ MESSAGES-DEBUG: Discussion orpheline détectée:', docSnapshot.id);
                continue;
              } else {
                console.log('✅ MESSAGES-DEBUG: Dossier existe, discussion valide');
              }
            } catch (error) {
              console.log('❌ MESSAGES-DEBUG: Erreur vérification dossier:', error);
              console.log('❌ MESSAGES-DEBUG: Discussion exclue par sécurité');
              continue;
            }
          } else {
            console.log('⚠️ MESSAGES-DEBUG: Discussion sans dossierId (acceptée)');
          }
          
          console.log('✅ MESSAGES-DEBUG: Discussion incluse (participant confirmé + dossier valide)');
          
          // Analyser chaque MESSAGE dans la discussion
          if (data.messages && Array.isArray(data.messages)) {
            console.log(`📨 MESSAGES-DEBUG: ${data.messages.length} messages dans la discussion`);
            
            let unreadInThisDiscussion = 0;
            let automaticInThisDiscussion = 0;
            
            data.messages.forEach((message, msgIndex) => {
              const isFromOtherUser = message.createdBy !== userId;
              const readBy = message.readBy || [];
              const isNotReadByUser = !readBy.includes(userId);
              
              // 🔧 FILTRAGE AMÉLIORÉ DES MESSAGES AUTOMATIQUES
              const isAutomaticMessage = 
                !message.createdBy || 
                message.createdBy === undefined || 
                message.createdBy === null ||
                message.createdBy === 'system' ||
                message.system === true ||
                message.automatic === true ||
                message.type === 'system' ||
                message.type === 'automatic';
              
              console.log(`  📨 MESSAGES-DEBUG: Message ${msgIndex + 1}:`);
              console.log(`    - Créé par: ${message.createdBy}`);
              console.log(`    - Texte: "${message.text?.substring(0, 30)}..."`);
              console.log(`    - ReadBy: [${readBy.join(', ')}]`);
              console.log(`    - isFromOtherUser: ${isFromOtherUser}`);
              console.log(`    - isNotReadByUser: ${isNotReadByUser}`);
              console.log(`    - isAutomaticMessage: ${isAutomaticMessage}`);
              console.log(`    - system: ${message.system}`);
              console.log(`    - automatic: ${message.automatic}`);
              console.log(`    - type: ${message.type}`);
              
              if (isAutomaticMessage) {
                console.log(`    - RÉSULTAT: 🤖 MESSAGE AUTOMATIQUE (EXCLU)`);
                automaticInThisDiscussion++;
                totalAutomaticMessages++;
              } else if (isFromOtherUser && isNotReadByUser) {
                console.log(`    - RÉSULTAT: 🔴 NON LU (COMPTÉ)`);
                unreadInThisDiscussion++;
                totalUnreadMessages++;
              } else if (!isFromOtherUser) {
                console.log(`    - RÉSULTAT: 👤 MESSAGE DE L'UTILISATEUR (NON COMPTÉ)`);
              } else {
                console.log(`    - RÉSULTAT: ✅ LU`);
              }
            });
            
            debugInfo.push({
              discussionId: docSnapshot.id,
              dossierId: data.dossierId,
              totalMessages: data.messages.length,
              automaticMessages: automaticInThisDiscussion,
              unreadMessages: unreadInThisDiscussion,
              dossierExists: data.dossierId ? 'vérifié' : 'sans dossier'
            });
            
            console.log(`📊 MESSAGES-DEBUG: Discussion ${docSnapshot.id}:`);
            console.log(`  - dossierId: ${data.dossierId}`);
            console.log(`  - ${data.messages.length} messages total`);
            console.log(`  - ${automaticInThisDiscussion} messages automatiques (exclus)`);
            console.log(`  - ${unreadInThisDiscussion} messages non lus (comptés)`);
          } else {
            console.log('📭 MESSAGES-DEBUG: Aucun message dans cette discussion');
          }
        }
        
        console.log('🎯 MESSAGES-DEBUG: *** RÉSUMÉ FINAL ***');
        console.log('🎯 MESSAGES-DEBUG: Résumé par discussion:', debugInfo);
        console.log('🎯 MESSAGES-DEBUG: TOTAL MESSAGES AUTOMATIQUES (exclus):', totalAutomaticMessages);
        console.log('🎯 MESSAGES-DEBUG: TOTAL MESSAGES NON LUS (comptés):', totalUnreadMessages);
        console.log('🎯 MESSAGES-DEBUG: Ancien count:', count);
        console.log('🎯 MESSAGES-DEBUG: Nouveau count:', totalUnreadMessages);
        
        if (count !== totalUnreadMessages) {
          console.log('🔥 MESSAGES-DEBUG: *** CHANGEMENT DÉTECTÉ ***');
          console.log(`🔥 MESSAGES-DEBUG: ${count} → ${totalUnreadMessages}`);
        } else {
          console.log('⚪ MESSAGES-DEBUG: Pas de changement dans le count');
        }
        
        setCount(totalUnreadMessages);
        setLoading(false);
        
      }, (error) => {
        console.error('❌ MESSAGES-DEBUG: *** ERREUR LISTENER ***');
        console.error('❌ MESSAGES-DEBUG:', error);
        
        if (error.code === 'failed-precondition' && error.message.includes('index')) {
          console.error('❌ MESSAGES-DEBUG: Index Firebase manquant!');
          console.error('❌ MESSAGES-DEBUG: Collection: discussions, Champs: participants + createdAt');
        }
        
        setLoading(false);
        setCount(0);
      });

    } catch (error) {
      console.error('❌ MESSAGES-DEBUG: Erreur d\'initialisation:', error);
      setLoading(false);
      setCount(0);
    }

    // Listener pour les événements de rafraîchissement  
    const handleRefresh = (data) => {
      console.log('🔄 MESSAGES-DEBUG: *** ÉVÉNEMENT RAFRAÎCHISSEMENT ***');
      console.log('🔄 MESSAGES-DEBUG: Données événement:', data);
      // Le onSnapshot se mettra à jour automatiquement
    };

    eventBus.on('forceRefreshNotificationCounts', handleRefresh);

    // Cleanup function
    return () => {
      console.log('🔌 MESSAGES-DEBUG: *** NETTOYAGE HOOK ***');
      if (unsubscribe) {
        unsubscribe();
      }
      eventBus.off('forceRefreshNotificationCounts', handleRefresh);
    };

  }, [userId, userEmail]);

  console.log('📊 MESSAGES-DEBUG: État actuel du hook - count:', count, 'loading:', loading);

  return { count, loading };
};

export default useUnreadMessagesCount;